import React from "react";
import { Flex, InfoIcon, Text, Tooltip } from "@fluentui/react-northstar";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { I18N_NS_LEAVE } from "../../../features/leave/constants";

/**
 * @typedef {Object} EmployeeData
 * @property {number} current_value - La valeur actuelle du solde de l'employé.
 * @property {string} name - Le nom du type de congé ou de solde.
 * @property {number} balance_value_to_remove - La valeur du solde à retirer, si applicable.
 * @property {string} period_start_date - La date de début de la période d'acquisition (format : AAAA-MM-JJ).
 * @property {string} period_end_date - La date de fin de la période d'acquisition (format : AAAA-MM-JJ).
 * @property {number} balance_on_end_period - Le solde prévu à la fin de la période.
 */

/**
 * CardSolde Component
 *
 * Ce composant affiche un résumé des soldes actuels pour les employés.
 * Si aucune donnée n'est fournie, un message indiquant l'absence de solde est affiché.
 *
 * @component
 * @param {Object} props - Les propriétés du composant.
 * @param {EmployeeData[]} [props.employee_data=[]] - Les données des employés.
 *
 * @returns {JSX.Element} Le composant JSX à rendre.
 *
 * @example
 * const employeeData = [
 *   {
 *     current_value: 10,
 *     name: "Congés payés",
 *     balance_value_to_remove: 2,
 *     period_start_date: "2023-01-01",
 *     period_end_date: "2023-12-31",
 *     balance_on_end_period: 8,
 *   },
 * ];
 *
 * <CardSolde employee_data={employeeData} />
 */
const CardSolde = ({ employee_data = [] }) => {
    const { t } = useTranslation();
    const isEmptyData = employee_data?.length === 0;
    return (
        <Flex column gap="gap.small">
            {isEmptyData ? (
                <Flex
                    style={{
                        marginBottom: "25px",
                        color: "#5B5FC7",
                        fontWeight: "bold",
                    }}
                    hAlign="center"
                >
                    <Text content="Aucun solde disponible" />
                </Flex>
            ) : (
                <Flex gap="gap.large" wrap>
                    {employee_data.map(
                        ({
                            current_value,
                            name,
                            balance_value_to_remove,
                            period_end_date,
                            period_start_date,
                            balance_on_end_period,
                        }) => {
                            return (
                                <Card vAlign="center">
                                    <Flex gap="gap.small" vAlign="center">
                                        <CardCurrentValue
                                            content={current_value}
                                        />
                                        {balance_on_end_period > 0 && (
                                            <Tooltip
                                                autoSize="width-always"
                                                content={
                                                    <Flex column>
                                                        <Text
                                                            content={
                                                                <Trans
                                                                    i18nKey={
                                                                        "translation.common.tooltips.acquisition"
                                                                    }
                                                                >
                                                                    Période
                                                                    d’acquisition
                                                                    : du{" "}
                                                                    <b>
                                                                        {{
                                                                            start_date:
                                                                                period_start_date,
                                                                        }}
                                                                    </b>{" "}
                                                                    au{" "}
                                                                    <b>
                                                                        {{
                                                                            end_date:
                                                                                period_end_date,
                                                                        }}
                                                                    </b>
                                                                </Trans>
                                                            }
                                                        />
                                                        <Text
                                                            content={
                                                                <Trans
                                                                    i18nKey={
                                                                        "translation.common.tooltips.prevision_solde"
                                                                    }
                                                                >
                                                                    Solde prévu
                                                                    au{" "}
                                                                    <b>
                                                                        {{
                                                                            end_date:
                                                                                period_end_date,
                                                                        }}
                                                                    </b>{" "}
                                                                    (dernier
                                                                    jour de la
                                                                    période) :{" "}
                                                                    <b>
                                                                        {{
                                                                            days: balance_on_end_period,
                                                                        }}{" "}
                                                                        j
                                                                    </b>
                                                                </Trans>
                                                            }
                                                        />
                                                    </Flex>
                                                }
                                                trigger={
                                                    <InfoIcon
                                                        outline
                                                        variables={{
                                                            color: "var(--color-primary)",
                                                            style: {
                                                                cursor: "pointer",
                                                            },
                                                        }}
                                                        size="large"
                                                    />
                                                }
                                                subtle={false}
                                                pointing
                                            />
                                        )}
                                    </Flex>
                                    <Flex>
                                        <LeaveTypeName content={name} />
                                    </Flex>
                                    <Flex>
                                        {balance_value_to_remove > 0 && (
                                            <PendingDays
                                                content={t(
                                                    "common.pending_days",
                                                    {
                                                        balance_value_to_remove,
                                                    }
                                                )}
                                            />
                                        )}
                                    </Flex>
                                </Card>
                            );
                        }
                    )}
                </Flex>
            )}
        </Flex>
    );
};

/**
 * @typedef {Object} CardSoldeWrapperProps
 * @property {React.ReactNode} children - Les éléments enfants à afficher dans le conteneur.
 * @property {string} title - Le titre à afficher en haut du conteneur.
 */

/**
 * CardSoldeWrapper Component
 *
 * Ce composant sert de conteneur pour afficher un titre et des enfants passés en tant que contenu.
 *
 * @component
 * @param {CardSoldeWrapperProps} props - Les propriétés du composant.
 * @returns {JSX.Element} Le composant JSX à rendre.
 *
 * @example
 * <CardSoldeWrapper title="Mon titre">
 *   <p>Contenu à afficher</p>
 * </CardSoldeWrapper>
 */
const CardSoldeWrapper = ({ children, title }) => {
    return (
        <Wrapper>
            <Title content={title} />
            {children}
        </Wrapper>
    );
};

CardSolde.CardSoldeWrapper = CardSoldeWrapper;

export default CardSolde;

const Wrapper = styled(Flex)`
    display: flex;
    padding: 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;

    border-radius: 8px;
    background: #fff;
`;

const PendingDays = styled(Text)`
    color: #cc4a31;

    /* Small/400 */
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 133.333% */
`;

const LeaveTypeName = styled(Text)`
    color: #424242;

    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px; /* 133.333% */
`;

const CardCurrentValue = styled(Text)`
    color: #5b5fc7;

    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 32px; /* 133.333% */
`;

const Title = styled(Text)`
    color: var(
        --light-theme-rest-foreground-default-foreground-242424,
        #242424
    );

    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px; /* 142.857% */
`;

const Card = styled(Flex)`
    border-left: 2px solid #f0f0f0;
    display: flex;
    width: 116px;
    padding: 8px 0px 8px 12px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    flex-shrink: 0;
`;
